import React from 'react';
import {
  Box, FormControl, InputLabel, Select, MenuItem, Typography, Chip, Divider, TextField, InputAdornment,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';
import PersonIcon from '@material-ui/icons/Person';
import LocalGasStationIcon from '@material-ui/icons/LocalGasStation';
import {
  setFuelType, setFuelConsumption, setFuelPrice,
  selectCurrentRoute,
} from '../../store/slices/routesSlice';
import { selectUI } from '../../store/slices/uiSlice';
import { t } from '../../i18n';
import SectionTitle from '../common/SectionTitle';
import GlassCard from '../common/GlassCard';
import { getCurrencySymbol, getSavedFuelPrice } from '../../utils/currencies';

// consumptionDefault is a generic truck fallback (L or kWh per 100km)
// The actual consumption comes from: user input > vehicle.fuelPer100km > this default
// The car/hgv adjustment (×0.38) is applied in RouteDetails and FuelCountryPanel
export const FUEL_TYPES = [
  { value: 'diesel',   labelKey: 'fuelDieselShort',   unit: 'L',   priceDefault: 1.65, co2PerUnit: 2.640, consumptionDefault: 25 },
  { value: 'gasoline', labelKey: 'fuelGasolineShort', unit: 'L',   priceDefault: 1.75, co2PerUnit: 2.310, consumptionDefault: 25 },
  { value: 'gnl',      labelKey: 'fuelGNL',           unit: 'kg',  priceDefault: 1.20, co2PerUnit: 2.750, consumptionDefault: 22 },
  { value: 'electric', labelKey: 'fuelElectric',      unit: 'kWh', priceDefault: 0.25, co2PerUnit: 0,     consumptionDefault: 80 },
];

const useStyles = makeStyles(() => ({
  root: { padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 16 },
  select: {
    '& .MuiSelect-root': { fontSize: '0.82rem', padding: '10px 12px' },
    '& .MuiInputLabel-root': { fontSize: '0.75rem' },
  },
  infoCard: {
    padding: 12,
    marginTop: 4,
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
    '&:last-child': { marginBottom: 0 },
  },
  infoLabel: { fontSize: '0.65rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' },
  infoValue: { fontSize: '0.78rem', color: '#0f172a', fontWeight: 500 },
  chip: {
    height: 20,
    fontSize: '0.62rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
  },
  fuelInputRow: {
    display: 'flex',
    gap: 8,
    marginTop: 10,
  },
  fuelInput: {
    flex: 1,
    '& .MuiInputBase-root': { fontSize: '0.82rem' },
    '& .MuiOutlinedInput-input': { padding: '8px 12px' },
    '& .MuiInputAdornment-root .MuiTypography-root': { fontSize: '0.72rem', color: '#64748b' },
  },
}));

function InfoRow({ label, value, chip, chipColor }) {
  const classes = useStyles();
  return (
    <Box className={classes.infoRow}>
      <Typography className={classes.infoLabel}>{label}</Typography>
      {chip ? (
        <Chip
          label={value}
          size="small"
          className={classes.chip}
          style={{
            background: `${chipColor || '#3b82f6'}18`,
            color: chipColor || '#3b82f6',
            border: `1px solid ${chipColor || '#3b82f6'}40`,
          }}
        />
      ) : (
        <Typography className={classes.infoValue}>{value || '—'}</Typography>
      )}
    </Box>
  );
}

function VehicleDriverForm() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const route = useSelector(selectCurrentRoute);
  const { lang } = useSelector(selectUI);

  const fuelType = route.fuelType || 'diesel';
  const fuelDef = FUEL_TYPES.find(f => f.value === fuelType) || FUEL_TYPES[0];
  const consumptionPlaceholder = fuelDef.consumptionDefault;

  const cd = route.computedRoute?.countryDistances;
  const isMultiCountry = cd && Object.keys(cd).length > 1;
  console.log('[VehicleDriverForm] countryDistances:', cd, 'isMultiCountry:', isMultiCountry);

  return (
    <Box className={classes.root}>
      {/* Driver Section — read-only, shows drivers assigned via Dispatch */}
      <Box>
        <SectionTitle icon={PersonIcon}>{t(lang, 'driver')}</SectionTitle>
        {route.routeDrivers?.length > 0 ? (
          <GlassCard className={classes.infoCard} flat>
            {route.routeDrivers.map((rd, i) => (
              <Box key={rd.driverId || i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
                <PersonIcon style={{ fontSize: 14, color: '#f59e0b' }} />
                <Typography style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1e293b', flex: 1 }}>
                  {rd.driverName || rd.name || '—'}
                </Typography>
                <Chip label={rd.status || 'assigned'} size="small"
                  style={{ height: 16, fontSize: '0.55rem', fontWeight: 700, background: '#dbeafe', color: '#1d4ed8' }} />
              </Box>
            ))}
          </GlassCard>
        ) : (
          <Typography style={{ fontSize: '0.72rem', color: '#94a3b8', fontStyle: 'italic', padding: '8px 0' }}>
            {lang === 'pt' ? 'Atribuir motoristas no Despacho' : 'Assign drivers in Dispatch'}
          </Typography>
        )}
      </Box>

      {/* Estes campos (tipo de combustível, consumo, preço) servem para CALCULAR
          o custo da rota — são parte do planeamento, não uma capacidade
          vendável à parte. O módulo 'fuel_management' foi removido do catálogo:
          o nome prometia gestão de combustível e só escondia estes campos. */}
      <>
        <Divider />

        {/* Fuel Section */}
        <Box>
          <SectionTitle icon={LocalGasStationIcon}>{t(lang, 'fuelSection')}</SectionTitle>

          <FormControl variant="outlined" fullWidth size="small" className={classes.select}>
            <InputLabel style={{ fontSize: '0.75rem' }}>{t(lang, 'fuelTypeLabel')}</InputLabel>
            <Select
              value={fuelType}
              onChange={e => dispatch(setFuelType(e.target.value))}
              label={t(lang, 'fuelTypeLabel')}
            >
              {FUEL_TYPES.map(f => (
                <MenuItem key={f.value} value={f.value}>
                  <span style={{ fontSize: '0.82rem' }}>{t(lang, f.labelKey)}</span>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box className={classes.fuelInputRow}>
            <TextField
              className={classes.fuelInput}
              variant="outlined"
              size="small"
              label={t(lang, 'consumptionPer100')}
              type="number"
              value={route.fuelConsumption ?? ''}
              placeholder={String(consumptionPlaceholder)}
              onChange={e => dispatch(setFuelConsumption(e.target.value !== '' ? Number(e.target.value) : null))}
              InputLabelProps={{ shrink: true, style: { fontSize: '0.75rem' } }}
              InputProps={{
                endAdornment: <InputAdornment position="end"><Typography>{fuelDef.unit}</Typography></InputAdornment>,
              }}
              inputProps={{ min: 0, step: 0.5 }}
            />
            {(() => {
              const curCode = route.routeOptions?.currency || 'EUR';
              const curSymbol = getCurrencySymbol(curCode);
              const localDefault = getSavedFuelPrice(curCode, fuelType);
              return (
                <TextField
                  className={classes.fuelInput}
                  variant="outlined"
                  size="small"
                  label={`${t(lang, 'fuelPriceLabel')} / ${fuelDef.unit}`}
                  type="number"
                  value={isMultiCountry ? '' : (route.fuelPrice ?? '')}
                  placeholder={isMultiCountry ? t(lang, 'pricePerCountry') : String(localDefault)}
                  onChange={e => dispatch(setFuelPrice(e.target.value !== '' ? Number(e.target.value) : null))}
                  disabled={!!isMultiCountry}
                  InputLabelProps={{ shrink: true, style: { fontSize: '0.75rem' } }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end"><Typography>{curSymbol}</Typography></InputAdornment>,
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              );
            })()}
          </Box>

          <GlassCard className={classes.infoCard} flat style={{ marginTop: 10 }}>
            <InfoRow label={t(lang, 'type')} value={t(lang, fuelDef.labelKey)} chip chipColor="#f59e0b" />
            <InfoRow
              label={`${t(lang, 'consumptionLbl')} (${fuelDef.unit}/100km)`}
              value={`${route.fuelConsumption ?? consumptionPlaceholder} ${fuelDef.unit}`}
            />
            {(() => {
              const curCode = route.routeOptions?.currency || 'EUR';
              const curSymbol = getCurrencySymbol(curCode);
              const pr = route.fuelPrice ?? getSavedFuelPrice(curCode, fuelType);
              return (
                <InfoRow
                  label={`${t(lang, 'fuelPriceLabel')} (${curSymbol}/${fuelDef.unit})`}
                  value={`${pr.toFixed(curCode === 'AOA' ? 0 : 3)} ${curSymbol}`}
                />
              );
            })()}
            {fuelDef.co2PerUnit > 0 && (
              <InfoRow label={t(lang, 'co2Label')} value={`${fuelDef.co2PerUnit} kg`} />
            )}
          </GlassCard>
        </Box>
      </>

    </Box>
  );
}

export default VehicleDriverForm;
